// Keyboard Shortcuts Hook (E007-S021) - Enhanced Implementation
import { useEffect, useCallback, useState } from 'react';

export interface ShortcutConfig {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  meta?: boolean;
  action: () => void;
  description: string;
  category?: 'navigation' | 'actions' | 'search' | 'editing';
  enabled?: boolean;
  context?: string; // Display context like "G + D"
}

// Check if user is typing in an input field
const isTypingInInput = (): boolean => {
  const activeElement = document.activeElement;
  if (!activeElement) return false;
  
  const tagName = activeElement.tagName.toLowerCase();
  const isInput = tagName === 'input' || tagName === 'textarea' || tagName === 'select';
  const isContentEditable = activeElement.getAttribute('contenteditable') === 'true';
  
  return isInput || isContentEditable;
};

export const useKeyboardShortcuts = (shortcuts: ShortcutConfig[]) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if user is typing in an input field
      if (isTypingInInput()) {
        // Allow Escape key even when typing
        if (event.key !== 'Escape') {
          return;
        }
      }

      shortcuts.forEach(shortcut => {
        // Check if shortcut is enabled (default to true)
        if (shortcut.enabled === false) return;

        // Check modifier keys
        const ctrlMatch = shortcut.ctrl 
          ? (event.ctrlKey || event.metaKey) 
          : (!event.ctrlKey && !event.metaKey);
        const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;
        const altMatch = shortcut.alt ? event.altKey : !event.altKey;
        
        if (
          event.key.toLowerCase() === shortcut.key.toLowerCase() &&
          ctrlMatch &&
          shiftMatch &&
          altMatch
        ) {
          event.preventDefault();
          shortcut.action();
        }
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts]);
};

// Two-key command mode hook (for G+D, G+C, etc.)
export const useCommandMode = () => {
  const [commandMode, setCommandMode] = useState<string | null>(null);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const enterCommandMode = useCallback((mode: string) => {
    // Clear any existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    setCommandMode(mode);
    
    // Auto-exit command mode after 2 seconds
    const newTimeoutId = setTimeout(() => {
      setCommandMode(null);
    }, 2000);
    
    setTimeoutId(newTimeoutId);
  }, [timeoutId]);

  const exitCommandMode = useCallback(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    setCommandMode(null);
  }, [timeoutId]);

  return { commandMode, enterCommandMode, exitCommandMode };
};

// Format shortcut for display
export const formatShortcut = (shortcut: ShortcutConfig): string => {
  if (shortcut.context) return shortcut.context;
  
  const parts: string[] = [];
  
  if (shortcut.ctrl || shortcut.meta) {
    // Show ⌘ on Mac, Ctrl on Windows
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    parts.push(isMac ? '⌘' : 'Ctrl');
  }
  if (shortcut.shift) parts.push('Shift');
  if (shortcut.alt) parts.push('Alt');
  
  // Format special keys
  let keyDisplay = shortcut.key;
  if (shortcut.key === 'ArrowUp') keyDisplay = '↑';
  else if (shortcut.key === 'ArrowDown') keyDisplay = '↓';
  else if (shortcut.key === 'ArrowLeft') keyDisplay = '←';
  else if (shortcut.key === 'ArrowRight') keyDisplay = '→';
  else if (shortcut.key === 'Escape') keyDisplay = 'Esc';
  else if (shortcut.key === 'Enter') keyDisplay = '⏎';
  else keyDisplay = shortcut.key.toUpperCase();
  
  parts.push(keyDisplay);
  
  return parts.join(' + ');
};

// Pre-configured shortcuts for common actions
export const commonShortcuts: ShortcutConfig[] = [
  // Search shortcuts
  {
    key: '/',
    action: () => {
      const searchInput = document.querySelector('input[type="search"], input[placeholder*="search" i]') as HTMLInputElement;
      searchInput?.focus();
    },
    description: 'Focus search',
    category: 'search',
  },
  {
    key: 'k',
    ctrl: true,
    action: () => {
      // Open command palette (trigger custom event)
      window.dispatchEvent(new CustomEvent('openCommandPalette'));
    },
    description: 'Open command palette',
    category: 'search',
  },
  {
    key: 'f',
    ctrl: true,
    action: () => {
      const searchInput = document.querySelector('input[type="search"], input[placeholder*="search" i]') as HTMLInputElement;
      searchInput?.focus();
    },
    description: 'Focus search',
    category: 'search',
  },
  {
    key: 'f',
    ctrl: true,
    shift: true,
    action: () => {
      // Open advanced filters
      window.dispatchEvent(new CustomEvent('openAdvancedFilters'));
    },
    description: 'Open advanced filters',
    category: 'search',
  },
  
  // Quick actions
  {
    key: 'r',
    action: () => {
      // Refresh data
      window.dispatchEvent(new CustomEvent('refreshData'));
    },
    description: 'Refresh data',
    category: 'actions',
  },
  {
    key: 'x',
    action: () => {
      // Export current view
      window.dispatchEvent(new CustomEvent('exportView'));
    },
    description: 'Export current view',
    category: 'actions',
  },
  {
    key: '?',
    action: () => {
      // Show shortcuts help
      window.dispatchEvent(new CustomEvent('showShortcutHelp'));
    },
    description: 'Show keyboard shortcuts',
    category: 'actions',
  },
  
  // Editing shortcuts
  {
    key: 's',
    ctrl: true,
    action: () => {
      // Save changes
      window.dispatchEvent(new CustomEvent('saveChanges'));
    },
    description: 'Save changes',
    category: 'editing',
  },
  {
    key: 'z',
    ctrl: true,
    action: () => {
      // Undo
      window.dispatchEvent(new CustomEvent('undo'));
    },
    description: 'Undo',
    category: 'editing',
  },
  {
    key: 'z',
    ctrl: true,
    shift: true,
    action: () => {
      // Redo
      window.dispatchEvent(new CustomEvent('redo'));
    },
    description: 'Redo',
    category: 'editing',
  },
  
  // Modal/dialog shortcuts
  {
    key: 'Escape',
    action: () => {
      // Close modal/drawer
      window.dispatchEvent(new CustomEvent('closeModal'));
    },
    description: 'Close modal/dialog',
    category: 'navigation',
  },
];

// Navigation shortcuts (used with two-key command mode)
export const navigationShortcuts: ShortcutConfig[] = [
  {
    key: 'd',
    action: () => {},
    description: 'Go to Dashboard',
    category: 'navigation',
    context: 'G + D',
  },
  {
    key: 'c',
    action: () => {},
    description: 'Go to Campaigns',
    category: 'navigation',
    context: 'G + C',
  },
  {
    key: 'r',
    action: () => {},
    description: 'Go to Reports',
    category: 'navigation',
    context: 'G + R',
  },
  {
    key: 's',
    action: () => {},
    description: 'Go to Settings',
    category: 'navigation',
    context: 'G + S',
  },
];

// Campaign list shortcuts
export const campaignListShortcuts: ShortcutConfig[] = [
  {
    key: 'j',
    action: () => {
      window.dispatchEvent(new CustomEvent('selectNextCampaign'));
    },
    description: 'Select next campaign',
    category: 'actions',
  },
  {
    key: 'ArrowDown',
    action: () => {
      window.dispatchEvent(new CustomEvent('selectNextCampaign'));
    },
    description: 'Select next campaign',
    category: 'actions',
    context: '↓',
  },
  {
    key: 'k',
    action: () => {
      window.dispatchEvent(new CustomEvent('selectPreviousCampaign'));
    },
    description: 'Select previous campaign',
    category: 'actions',
  },
  {
    key: 'ArrowUp',
    action: () => {
      window.dispatchEvent(new CustomEvent('selectPreviousCampaign'));
    },
    description: 'Select previous campaign',
    category: 'actions',
    context: '↑',
  },
  {
    key: 'Enter',
    action: () => {
      window.dispatchEvent(new CustomEvent('openSelectedCampaign'));
    },
    description: 'Open selected campaign',
    category: 'actions',
  },
  {
    key: 'e',
    action: () => {
      window.dispatchEvent(new CustomEvent('editSelectedCampaign'));
    },
    description: 'Edit selected campaign',
    category: 'actions',
  },
  {
    key: 'p',
    action: () => {
      window.dispatchEvent(new CustomEvent('pauseSelectedCampaign'));
    },
    description: 'Pause/unpause campaign',
    category: 'actions',
  },
  {
    key: 'd',
    action: () => {
      window.dispatchEvent(new CustomEvent('duplicateSelectedCampaign'));
    },
    description: 'Duplicate campaign',
    category: 'actions',
  },
];

// Get all shortcuts grouped by category
export const getAllShortcuts = (): Record<string, ShortcutConfig[]> => {
  const allShortcuts = [
    ...commonShortcuts,
    ...navigationShortcuts,
    ...campaignListShortcuts,
  ];
  
  return allShortcuts.reduce((acc, shortcut) => {
    const category = shortcut.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(shortcut);
    return acc;
  }, {} as Record<string, ShortcutConfig[]>);
};
